import { DestroyRef, ErrorHandler, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, PRIMARY_OUTLET, Router } from '@angular/router';
import { filter } from 'rxjs';
import { SUBSCRIPTION_PATH } from 'src/app/shared/constants/route.constants';
import { getPJStepPaths } from 'src/app/shared/utils/pj-subscription.factory';
import { GtmEventServiceService } from './gtm-event-service.service';
import { CombinedStore } from 'src/app/core/combined.store';

@Injectable({ providedIn: 'root' })
export class FunnelTrackingService {
  private readonly router = inject(Router);
  private readonly store = inject(CombinedStore);
  private readonly gtmEventService = inject(GtmEventServiceService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly errorHandler = inject(ErrorHandler);
  private readonly stepPaths = getPJStepPaths(this.store.context.avenants != null);
  private started = false;
  private previousStep: string | undefined;

  start(): void {
    if (this.started) return;
    this.started = true;

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.trackCurrentStep());

    if (this.router.navigated) this.trackCurrentStep();
  }

  private trackCurrentStep(): void {
    const stepPath = this.getCurrentStepPath();
    const stepName = stepPath && this.stepPaths.has(stepPath) ? stepPath : undefined;
    if (stepName === this.previousStep) return;
    this.previousStep = stepName;

    if (stepName) {
      void this.gtmEventService.pushEvent('funnel_step', { step_name: stepName })
        .catch((error: unknown) => this.errorHandler.handleError(error));
    }
  }

  private getCurrentStepPath(): string | undefined {
    let route = this.router.routerState.snapshot.root;
    while (route) {
      const child = route.children.find((candidate) => candidate.outlet === PRIMARY_OUTLET);
      if (route.routeConfig?.path === SUBSCRIPTION_PATH) return child?.routeConfig?.path;
      if (!child) return undefined;
      route = child;
    }
    return undefined;
  }
}
